import { Modal } from 'components/Modal';
import { Title } from 'components/Title';
import modalStyles from './BeneficiariesUpdateModal.module.scss';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { Button } from 'ui/Button';
import { useUpdateBeneficiarCommissionMutation } from 'redux/Api/beneficiariesApi/beneficiariesApi';

interface ModalUpdateCommissionProps {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
}

export default function ModalUpdateCommission({
  id,
  isOpen,
  onClose,
  refetch,
}: ModalUpdateCommissionProps) {
  const { infoBeneficiar } = useSelector((state: RootState) => state.beneficiarSlice);
  const [pendingCommissionData, setPendingCommissionData] = useState<{
    commission_percent: number | string;
    commission_min_amount: number | string;
    commission_end_date: string;
  } | null>(null);
  const [confirmSaveCommissionOpen, setConfirmSaveCommissionOpen] = useState(false);
  const [updateBeneficiarCommission, { isLoading: isUpdatingComission }] =
    useUpdateBeneficiarCommissionMutation();
  const [showError, setShowError] = useState(false);
  const [errorText, setErrorText] = useState<string>('');

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      commission_percent: infoBeneficiar?.commission?.percent || 0,
      commission_min_amount: infoBeneficiar?.commission?.min_amount || 0,
      commission_end_date: infoBeneficiar?.commission?.end_date || '',
    },
  });

  useEffect(() => {
    if (infoBeneficiar && isOpen) {
      setValue('commission_percent', infoBeneficiar?.commission?.percent || 0);
      setValue('commission_min_amount', infoBeneficiar?.commission?.min_amount || 0);
      setValue('commission_end_date', infoBeneficiar?.commission?.end_date || '');
    }
  }, [infoBeneficiar, isOpen, setValue]);

  const onSubmitCommission = (formData: {
    commission_percent: number | string;
    commission_min_amount: number | string;
    commission_end_date: string;
  }) => {
    setPendingCommissionData(formData);
    setConfirmSaveCommissionOpen(true);
  };

  const confirmSaveCommission = async () => {
    if (!id || !pendingCommissionData) return;

    const expectedPassword = process.env.REACT_APP_PASSWORD_UPDATE_PROFILE || '';

    const enteredPassword = window.prompt('Введите пароль для подтверждения изменений');

    if (enteredPassword === null) {
      return;
    }

    if (enteredPassword !== expectedPassword) {
      window.alert('Неверный пароль');
      return;
    }

    try {
      const body = {
        percent: Number(pendingCommissionData.commission_percent) || 0,
        min_amount: Number(pendingCommissionData.commission_min_amount) || 0,
        end_date: pendingCommissionData.commission_end_date
          ? pendingCommissionData.commission_end_date
          : null,
      };

      await updateBeneficiarCommission({ id, body }).unwrap();
      await refetch();
      onClose();
      setConfirmSaveCommissionOpen(false);
      setPendingCommissionData(null);
    } catch (error: unknown) {
      const apiError = error as { data?: { message?: string } };
      setConfirmSaveCommissionOpen(false);
      setErrorText(apiError?.data?.message || 'Ошибка при обновлении данных заказчика');
      setShowError(true);
    }
  };

  const handleClose = () => {
    setPendingCommissionData(null);
    setConfirmSaveCommissionOpen(false);
    onClose();
    setErrorText('');
    setShowError(false);
  };
  return (
    <>
      {isOpen && (
        <Modal setOpen={handleClose}>
          <div className={modalStyles.root}>
            <Title className={modalStyles.title} title='Изменение комиссии' />
            <form autoComplete='off' onSubmit={handleSubmit(onSubmitCommission)}>
              <div className={modalStyles.rootInput}>
                <input
                  className={modalStyles.input}
                  type='number'
                  step='0.01'
                  {...register('commission_percent', {
                    required: 'Поле обязательно',
                    min: {
                      value: 0,
                      message: 'Минимум 0',
                    },
                  })}
                />
                <span className={modalStyles.rootInput_placeholder}>
                  Процент комиссии (%)
                </span>
                {errors.commission_percent && (
                  <p className={modalStyles.errors}>
                    {errors.commission_percent?.message as string}
                  </p>
                )}
              </div>

              <div className={modalStyles.rootInput}>
                <input
                  className={modalStyles.input}
                  type='number'
                  step='0.01'
                  {...register('commission_min_amount', {
                    required: 'Поле обязательно',
                    min: {
                      value: 0,
                      message: 'Минимум 0',
                    },
                  })}
                />
                <span className={modalStyles.rootInput_placeholder}>
                  Минимальная сумма комиссии (₽)
                </span>
                {errors.commission_min_amount && (
                  <p className={modalStyles.errors}>
                    {errors.commission_min_amount?.message as string}
                  </p>
                )}
              </div>

              <div className={modalStyles.rootInput}>
                <input
                  className={modalStyles.input}
                  type='date'
                  {...register('commission_end_date')}
                />
                <span className={modalStyles.rootInput_placeholder}>
                  Дата окончания (необязательно)
                </span>
                {errors.commission_end_date && (
                  <p className={modalStyles.errors}>
                    {errors.commission_end_date?.message as string}
                  </p>
                )}
              </div>

              {showError && <p style={{ color: 'red' }}>{errorText}</p>}

              <div className={modalStyles.buttons}>
                <Button
                  onClick={handleClose}
                  className={modalStyles.button_cancel}
                  theme='outline'>
                  Отмена
                </Button>
                <Button
                  disabled={isUpdatingComission}
                  loading={isUpdatingComission}
                  onClick={handleSubmit(onSubmitCommission)}
                  className={modalStyles.button}
                  theme='filed'>
                  Сохранить
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
      {confirmSaveCommissionOpen && (
        <Modal setOpen={setConfirmSaveCommissionOpen}>
          <div className={modalStyles.root}>
            <Title className={modalStyles.title} title='Подтверждение сохранения' />
            <p style={{ marginBottom: '20px', fontSize: '14px' }}>
              Вы уверены, что хотите сохранить изменения комиссии?
            </p>
            <div className={modalStyles.buttons}>
              <Button
                onClick={() => setConfirmSaveCommissionOpen(false)}
                className={modalStyles.button_cancel}
                theme='outline'>
                Отмена
              </Button>
              <Button
                disabled={isUpdatingComission}
                loading={isUpdatingComission}
                onClick={confirmSaveCommission}
                className={modalStyles.button}
                theme='filed'>
                Подтвердить
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
